/*global jQuery:readonly */
import Bloodhound from "bloodhound-js";
import * as H from "history";
import * as React from "react";
import { NotificationManager } from "react-notifications";

import { Place, PlaceGroup, Region, UserSummary } from "../data";
import { notifyServerErrorOrDefault } from "../helpers";

interface NewRegionItemEditorProps {
    globalMods: UserSummary[];
    isGlobalMod: boolean;
    onClose: () => void;
    onUpdate: (region: Region) => void;
    parent: Region | null;
}

interface NewRegionItemEditorState {
    name: string;
    moderators: UserSummary[];
}

class NewRegionItemEditor extends React.Component<NewRegionItemEditorProps, NewRegionItemEditorState> {
    state: NewRegionItemEditorState = {
        name: "",
        moderators: []
    };

    private agentSearch = new Bloodhound({
        identify: (obj): any => obj.id,
        datumTokenizer: Bloodhound.tokenizers.obj.whitespace("name"),
        queryTokenizer: Bloodhound.tokenizers.whitespace,
        indexRemote: true,
        remote: {
            url: "/backend/agent?q=%QUERY",
            wildcard: "%QUERY"
        }
    });

    constructor(props: NewRegionItemEditorProps) {
        super(props);

        this.handleCancel = this.handleCancel.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleSave = this.handleSave.bind(this);
    }

    get moderators(): JSX.Element[] {
        const result = [];
        result.push.apply(result, this.props.globalMods.map((u): JSX.Element => {
            return <li key={ u.id }>@{u.codename} (GLOBAL)</li>;
        }));

        if (this.props.parent) {
            result.push.apply(result, this.props.parent.moderators.map((u): JSX.Element => {
                return <li key={ u.id }>@{u.codename} (PARENT)</li>;
            }));
        }

        result.push.apply(result, this.state.moderators.map((u): JSX.Element => {
            return (
                <li key={ u.id }>
          @{u.codename}&nbsp;
                    <a
                        onClick={ (): void => {
                            this.setState((s: NewRegionItemEditorState): NewRegionItemEditorState => {
                                return { ...s, moderators: s.moderators.filter((i): boolean => i.id !== u.id) };
                            });
                        } }
                    >
                        <span className="glyphicon glyphicon-trash" /> Remove moderator
                    </a>
                </li>
            );
        }));

        return result;
    }

    componentDidMount(): void {
        const input = jQuery("#moderator");
        input.typeahead(
            {
                minLength: 2,
                highlight: true,
                hint: false
            },
            {
                async: true,
                displayKey: "name",
                limit: 10,
                source: this.agentSearch.ttAdapter()
            });
        input.on("typeahead:select", (event, item): void => {
            this.setState((s: NewRegionItemEditorState): NewRegionItemEditorState => {
                const moderators = s.moderators.slice();
                moderators.push({ id: +item.id, codename: item.name });
                input.typeahead("val", "");

                return { ...s, moderators };
            });
        });
    }

    handleCancel(e: React.SyntheticEvent<HTMLButtonElement>): void {
        e.preventDefault();
        this.props.onClose();
    }

    handleChange(e: React.SyntheticEvent<HTMLInputElement>): void {
        const target = e.currentTarget;
        const value = target.type === "checkbox" ? target.checked : target.value;
        this.setState((state: NewRegionItemEditorState): NewRegionItemEditorState => ({ ...state, [target.name]: value }));
    }

    handleSave(e: React.SyntheticEvent<HTMLFormElement>): void {
        e.preventDefault();
        const update = {
            name: this.state.name,
            moderators: this.state.moderators.map((i): number => i.id),
            parent_id: this.props.parent ? this.props.parent.id : 0,
        };

        jQuery.post("/backend/toolbox/regions", update).then(
            (data): void => {
                NotificationManager.success(`Added region ${this.state.name}`);
                this.props.onUpdate({
                    id: data.region.id,
                    indirectModerators: data.region.indirect_moderators,
                    moderators: data.region.moderators,
                    name: data.region.name,
                    parentId: data.region.parent_id,
                    type: data.region.type
                });
            },
            (ajax): void => {
                notifyServerErrorOrDefault(ajax, `Could not add region ${this.state.name}`);
            });
    }

    render(): JSX.Element {
        const region = this.state;
        const isGlobalMod = this.props.isGlobalMod;
        const parent = this.props.parent;

        return (
            <form onSubmit={ this.handleSave }>
                <h3>Add {parent ? `Region to ${parent.name}` : "Country"}</h3>

                <div className="form-group">
                    <label htmlFor="name">name</label>
                    <input
                        type="text"
                        name="name"
                        className="form-control"
                        onChange={ this.handleChange }
                        value={ region.name }
                    />
                </div>

                {isGlobalMod && <div className="form-group">
                    <label htmlFor="moderator">Moderators</label>
                    <ul>
                        {this.moderators}
                    </ul>
                    <label htmlFor="moderator">Add moderator</label>
                    <div>
                        <input id="moderator" type="text" className="form-control" />
                    </div>
                </div>}

                <div className="form-group">
                    <div className="btn-group" role="group" aria-label="Region actions">
                        <button type="submit" className="btn btn-primary">Save</button>
                        <button type="button" className="btn btn-default" onClick={ this.handleCancel }>Cancel</button>
                    </div>
                </div>
            </form>
        );
    }
}

interface RegionItemEditorProps {
    globalMods: UserSummary[];
    isGlobalMod: boolean;
    onClose: () => void;
    onUpdate: (region: Region) => void;
    places: PlaceGroup[];
    region: Region;
}

interface RegionItemEditorState extends Region {
    mergeDestinationId: number;
    moderators: UserSummary[];
}

class RegionItemEditor extends React.Component<RegionItemEditorProps, RegionItemEditorState> {
    state: RegionItemEditorState;

    private agentSearch = new Bloodhound({
        identify: (obj): any => obj.id,
        datumTokenizer: Bloodhound.tokenizers.obj.whitespace("name"),
        queryTokenizer: Bloodhound.tokenizers.whitespace,
        indexRemote: true,
        remote: {
            url: "/backend/agent?q=%QUERY",
            wildcard: "%QUERY"
        }
    });

    constructor(props: RegionItemEditorProps) {
        super(props);

        this.state = Object.assign(
            { mergeDestinationId: 0 },
            this.props.region as RegionItemEditorState);

        this.handleCancel = this.handleCancel.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleMergeRegion = this.handleMergeRegion.bind(this);
        this.handleMergeRegionConfirmation = this.handleMergeRegionConfirmation.bind(this);
        this.handleSave = this.handleSave.bind(this);
    }

    get moderators(): JSX.Element[] {
        const result = [];
        result.push.apply(result, this.props.globalMods.map((u): JSX.Element => {
            return <li key={ u.id }>@{u.codename} (GLOBAL)</li>;
        }));

        result.push.apply(result, this.state.indirectModerators.map((u): JSX.Element => {
            return <li key={ u.id }>@{u.codename} (PARENT)</li>;
        }));

        result.push.apply(result, this.state.moderators.map((u): JSX.Element => {
            return (
                <li key={ u.id }>
          @{u.codename}&nbsp;
                    <a
                        onClick={ (): void => {
                            this.setState((s: RegionItemEditorState): RegionItemEditorState => {
                                return { ...s, moderators: s.moderators.filter((i): boolean => i.id !== u.id) };
                            });
                        } }
                    >
                        <span className="glyphicon glyphicon-trash" /> Remove moderator
                    </a>
                </li>
            );
        }));

        return result;
    }

    get placeOptions(): JSX.Element[] | null {
        const country = this.props.places
            .find((c): boolean => c.name === this.state.parentId);

        if (!country) {
            return null;
        }

        return country.place.filter((i): boolean => i.id !== this.state.id).map((place): JSX.Element => {
            return (
                <option value={ place.id } key={ place.id }>{place.name}</option>
            );
        });
    }

    get placeSelect(): JSX.Element | null {
        return (
            <div className="form-group">
                <label htmlFor="mergeDestination">City</label>
                <select
                    id="mergeDestination"
                    className="form-control"
                    data-live-search="true"
                    title="City/area"
                    data-size="7"
                >
                    {this.placeOptions}
                </select>
            </div>
        );
    }

    componentDidMount(): void {
        jQuery("#mergeDestination").selectpicker();
        jQuery("#mergeDestination").on("changed.bs.select", (e): void => {
            this.setState({
                mergeDestinationId: +e.target.value
            });
        });

        const input = jQuery("#moderator");
        input.typeahead(
            {
                minLength: 2,
                highlight: true,
                hint: false
            },
            {
                async: true,
                displayKey: "name",
                limit: 10,
                source: this.agentSearch.ttAdapter()
            });
        input.on("typeahead:select", (event, item): void => {
            this.setState((s: RegionItemEditorState): RegionItemEditorState => {
                const moderators = s.moderators.slice();
                moderators.push({ id: +item.id, codename: item.name });
                input.typeahead("val", "");

                return { ...s, moderators };
            });
        });
    }

    componentDidUpdate(prevProps: RegionItemEditorProps, prevState: RegionItemEditorState): void {
        if (prevProps.places !== this.props.places ||
        prevState.parentId !== this.state.parentId) {
            jQuery("#mergeDestination").selectpicker("refresh");
        }
    }

    handleCancel(e: React.SyntheticEvent<HTMLButtonElement>): void {
        e.preventDefault();
        this.props.onClose();
    }

    handleChange(e: React.SyntheticEvent<HTMLInputElement>): void {
        const target = e.currentTarget;
        const value = target.type === "checkbox" ? target.checked : target.value;
        this.setState((): any => ({ [target.name]: value }));
    }

    handleMergeRegionConfirmation(): void {
        jQuery("#confirmation").modal("show");
    }

    handleMergeRegion(e: React.SyntheticEvent<HTMLButtonElement>): void {
        e.preventDefault();
        jQuery("#confirmation").modal("hide");
        jQuery.post(`/backend/toolbox/regions/${this.state.id}/merge/${this.state.mergeDestinationId}`).then(
            (data): void => {
                NotificationManager.success(`Merged region ${this.state.name} into ${data.region.name}`);
                this.props.onClose();
            },
            (ajax): void => {
                notifyServerErrorOrDefault(ajax, `Could not merge region ${this.state.name}`);
            });
    }

    handleSave(e: React.SyntheticEvent<HTMLFormElement>): void {
        e.preventDefault();
        const update = {
            name: this.state.name,
            moderators: this.state.moderators.map((i): number => i.id)
        };

        jQuery.post(`/backend/toolbox/regions/${this.state.id}`, update).then(
            (data): void => {
                NotificationManager.success(`Updated region ${this.state.name}`);
                this.props.onUpdate({
                    id: data.region.id,
                    indirectModerators: data.region.indirect_moderators,
                    moderators: data.region.moderators,
                    name: data.region.name,
                    parentId: data.region.parent_id,
                    type: data.region.type
                });
            },
            (ajax): void => {
                notifyServerErrorOrDefault(ajax, `Could not update region ${this.state.name}`);
            });
    }

    render(): JSX.Element {
        const region = this.state;
        const canDeleteRegions = this.props.isGlobalMod && !!region.parentId;
        const isGlobalMod = this.props.isGlobalMod;

        return (
            <div>
                <div className="modal fade" id="confirmation" role="dialog" aria-labelledby="modalLabel">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                                    <span aria-hidden="true">&times;</span>
                                </button>
                                <h4 className="modal-title" id="modalLabel">Merge users into region</h4>
                            </div>
                            <div className="modal-body">
                                {this.placeSelect}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-default" data-dismiss="modal">Cancel</button>
                                <button type="button" onClick={ this.handleMergeRegion } className="btn btn-danger">Merge</button>
                            </div>
                        </div>
                    </div>
                </div>

                <form onSubmit={ this.handleSave }>
                    <h3>Update Region</h3>

                    <div className="form-group">
                        <label htmlFor="name">name</label>
                        <input
                            type="text"
                            name="name"
                            className="form-control"
                            onChange={ this.handleChange }
                            value={ region.name }
                        />
                    </div>

                    {isGlobalMod && <div className="form-group">
                        <label htmlFor="moderator">Moderators</label>
                        <ul>
                            {this.moderators}
                        </ul>
                        <label htmlFor="moderator">Add moderator</label>
                        <div>
                            <input id="moderator" type="text" className="form-control" />
                        </div>
                    </div>}

                    <div className="form-group">
                        <div className="btn-group" role="group" aria-label="Region actions">
                            <button type="submit" className="btn btn-primary">Save</button>
                            <button type="button" className="btn btn-default" onClick={ this.handleCancel }>Cancel</button>
                        </div>

                        {canDeleteRegions && <div className="btn-group pull-right" role="group" aria-label="Region actions">
                            <button type="button" className="btn btn-danger" onClick={ this.handleMergeRegionConfirmation }>
                                <span className="glyphicon glyphicon-trash" /> Delete Region
                            </button>
                        </div>}
                    </div>
                </form>
            </div>
        );
    }
}

interface RegionEditorProps {
    countryId: string | undefined;
    countries: Place[];
    history: H.History;
    globalMods: UserSummary[];
    isGlobalMod: boolean;
    modRegions: {[key: number]: boolean};
    placeId: string | undefined;
    places: PlaceGroup[];
    regionStats: {[key: number]: number};
}

interface RegionEditorState {
    addRegion: boolean;
    countryId: number;
    loading: boolean;
    placeId: number;
    region: Region | null;
}

class RegionEditor extends React.Component<RegionEditorProps, RegionEditorState> {
    state: RegionEditorState;

    constructor(props: RegionEditorProps) {
        super(props);

        this.state = {
            addRegion: false,
            loading: false,
            countryId: props.countryId && !isNaN(+props.countryId) ? +props.countryId : 0,
            placeId: props.placeId && !isNaN(+props.placeId) ? +props.placeId : 0,
            region: null
        };

        this.handleAddCountryEvent = this.handleAddCountryEvent.bind(this);
        this.handleAddRegionEvent = this.handleAddRegionEvent.bind(this);
        this.handleCloseRegion = this.handleCloseRegion.bind(this);
        this.handleCountryLookupEvent = this.handleCountryLookupEvent.bind(this);
        this.handleRegionAdded = this.handleRegionAdded.bind(this);
        this.handleRegionLookupEvent = this.handleRegionLookupEvent.bind(this);

        if (this.state.countryId) {
            if (this.state.placeId) {
                this.lookupRegion(this.state.countryId, this.state.placeId);
            } else {
                this.lookupRegion(this.state.countryId);
            }
        }
    }

    get countryOptions(): JSX.Element[] {
        return this.props.countries
            .filter((i): boolean => this.props.isGlobalMod || this.props.modRegions[i.id])
            .map((country): JSX.Element => {
                const regionStats = this.props.regionStats;
                const stats = regionStats && country.id > 0 ? ` (${this.props.regionStats[country.id] || 0})` : "";

                return (
                    <option value={ country.id } key={ country.id }>
                        {country.name}{stats}
                    </option>
                );
            });
    }

    get placeOptions(): JSX.Element[] | null {
        const country = this.props.places
            .find((c): boolean => c.name === this.state.countryId);

        if (!country) {
            return null;
        }

        return country.place.map((place): JSX.Element => {
            const regionStats = this.props.regionStats;
            const stats = regionStats && place.id > 0 ? ` (${this.props.regionStats[place.id] || 0})` : "";

            return (
                <option value={ place.id } key={ place.id }>
                    {place.name}{stats}
                </option>
            );
        });
    }

    get region(): JSX.Element | null {
        if (this.state.loading) {
            return (
                <div className="row">
                    <div className="col-xs-12">
                        <p>Loading...</p>
                    </div>
                </div>
            );
        }

        if (this.state.region === null && !this.state.addRegion) {
            return null;
        }

        if (this.state.addRegion) {
            return (
                <div className="row">
                    <div className="col-xs-12">
                        <NewRegionItemEditor
                            globalMods={ this.props.globalMods }
                            isGlobalMod={ this.props.isGlobalMod }
                            parent={ this.state.region }
                            onClose={ (): void => this.setState({ region: null, addRegion: false }) }
                            onUpdate={ this.handleRegionAdded }
                        />
                    </div>
                </div>
            );
        } else {
            return (
                <div className="row">
                    <div className="col-xs-12">
                        <RegionItemEditor
                            globalMods={ this.props.globalMods }
                            isGlobalMod={ this.props.isGlobalMod }
                            places={ this.props.places }
                            region={ this.state ? this.state.region : "" }
                            onClose={ this.handleCloseRegion }
                            onUpdate={ (r: Region): void => this.setState({ region: r }) }
                        />
                    </div>
                </div>
            );
        }
    }

    componentDidMount(): void {
        jQuery("#country, #place, #quickfilter").selectpicker();
        jQuery("#country").on("changed.bs.select", (e): void => {
            jQuery("#place").val(0);

            this.setState({
                countryId: +e.target.value,
                placeId: 0,
            });
        });

        jQuery("#place").on("changed.bs.select", (e): void => {
            this.setState({
                placeId: +e.target.value,
            });
        });
    }

    componentDidUpdate(prevProps: RegionEditorProps, prevState: RegionEditorState): void {
        if (prevProps.countries !== this.props.countries ||
        prevProps.modRegions !== this.props.modRegions ||
        prevProps.regionStats !== this.props.regionStats) {
            jQuery("#country").selectpicker("refresh");

            if (this.state.countryId) {
                jQuery("#country").selectpicker("val", String(this.state.countryId));
            }
        }

        if (prevProps.places !== this.props.places ||
        prevState.countryId !== this.state.countryId ||
        prevProps.regionStats !== this.props.regionStats) {
            jQuery("#place").selectpicker("refresh");

            if (this.state.placeId) {
                jQuery("#place").selectpicker("val", String(this.state.placeId));
            }
        }

        if (prevProps.countryId !== this.props.countryId ||
        prevProps.placeId !== this.props.placeId) {

            const countryId = this.props.countryId && !isNaN(+this.props.countryId) ? +this.props.countryId : 0;
            const placeId = this.props.placeId && !isNaN(+this.props.placeId) ? +this.props.placeId : 0;
            this.setState({ countryId, placeId });
            jQuery("#country").selectpicker("val", String(countryId));
            jQuery("#place").selectpicker("val", String(placeId));
            if (countryId) {
                if (placeId) {
                    this.lookupRegion(countryId, placeId);
                } else {
                    this.lookupRegion(countryId);
                }
            }
        }
    }

    handleAddCountryEvent(e: React.SyntheticEvent<HTMLButtonElement>): void {
        e.preventDefault();
        this.setState({ addRegion: true, region: null });
    }

    handleAddRegionEvent(e: React.SyntheticEvent<HTMLButtonElement>): void {
        e.preventDefault();
        this.setState({ addRegion: true });
        this.lookupRegion(this.state.countryId);
    }

    handleCloseRegion(): void {
        this.setState({ region: null });
    }

    handleCountryLookupEvent(e: React.SyntheticEvent<HTMLFormElement>): void {
        e.preventDefault();
        this.props.history.push(`/toolbox/regioneditor/${this.state.countryId}`);
        this.setState({ addRegion: false });
        this.lookupRegion(this.state.countryId);
    }

    handleRegionAdded(region: Region): void {
        this.setState({ addRegion: false, region: region });

        if (region.parentId === 0) {
            this.props.history.push(`/toolbox/regioneditor/${region.id}`);
        } else {
            this.props.history.push(`/toolbox/regioneditor/${region.parentId}/${region.id}`);
        }
    }

    handleRegionLookupEvent(e: React.SyntheticEvent<HTMLFormElement>): void {
        e.preventDefault();
        this.props.history.push(`/toolbox/regioneditor/${this.state.countryId}/${this.state.placeId}`);
        this.setState({ addRegion: false });
        this.lookupRegion(this.state.countryId, this.state.placeId);
    }

    lookupRegion(countryId: number, placeId?: number): void {
        this.setState({ loading: true });
        jQuery.getJSON(`/backend/toolbox/regions/${placeId || countryId}`).then(
            (data): void => {
                this.setState((state: RegionEditorState): RegionEditorState => {
                    if (state.countryId === countryId &&
              (placeId === undefined || state.placeId === placeId)
                    ) {

                        return {
                            ...state,
                            loading: false,
                            region: {
                                id: data.region.id,
                                indirectModerators: data.region.indirect_moderators,
                                moderators: data.region.moderators,
                                name: data.region.name,
                                parentId: data.region.parent_id,
                                type: data.region.type
                            }
                        };
                    }

                    return { ...state };
                });
            },
            (ajax): void => {
                notifyServerErrorOrDefault(ajax, "Could not load region");
            });
    }

    render(): JSX.Element {
        const mods = this.props.globalMods
            .map((i): string => `@${i.codename}`)
            .join(", ") || "No one";

        return (
            <div>
                <div className="row" id="header">
                    <div className="col-xs-12">
                        <h2>Region Editor</h2>
                    </div>
                </div>

                <div className="row">
                    <div className="col-xs-12">
                        <form className="form-horizontal" onSubmit={ this.handleCountryLookupEvent }>
                            <div className="form-group">
                                <label htmlFor="country" className="control-label col-xs-2">Country:</label>
                                <div className="col-xs-3">
                                    <select
                                        id="country"
                                        className="form-control"
                                        data-live-search="true"
                                        title="Country"
                                        data-size="7"
                                    >
                                        {this.countryOptions}
                                    </select>
                                </div>

                                <div className="btn-group col-xs-7">
                                    {this.props.isGlobalMod && <button type="button" className="btn btn-primary" onClick={ this.handleAddCountryEvent }>
                                        <span className="glyphicon glyphicon-plus" /> Add
                                    </button>}
                                    <button type="submit" className="btn btn-default" disabled={ this.state.countryId === 0 }>
                    Open country
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                <div className="row">
                    <div className="col-xs-12">
                        <form className="form-horizontal" onSubmit={ this.handleRegionLookupEvent }>
                            <div className="form-group">
                                <label htmlFor="place" className="control-label col-xs-2">Region:</label>
                                <div className="col-xs-3">
                                    <select
                                        id="place"
                                        className="form-control"
                                        data-live-search="true"
                                        title="City/area"
                                        data-size="7"
                                    >
                                        {this.placeOptions}
                                    </select>
                                </div>

                                <div className="col-xs-7">
                                    <div className="btn-group">
                                        <button type="button" className="btn btn-primary" disabled={ this.state.countryId === 0 } onClick={ this.handleAddRegionEvent }>
                                            <span className="glyphicon glyphicon-plus" /> Add
                                        </button>
                                        <button type="submit" className="btn btn-default" disabled={ this.state.placeId === 0 }>
                      Open region
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                <div className="row">
                    <div className="col-xs-12">
                        <p>Global mods (GM) are: {mods}</p>
                    </div>
                </div>

                <div className="row">
                    <div className="col-xs-12"><hr /></div>
                </div>

                {this.region}
            </div>
        );
    }
}

export default RegionEditor;
